import {Box} from '@mui/material';
import {t} from 'i18next';
import _ from 'lodash';
import AurDataGridCellWrapper from '../../common/aur-data-grid/AurDataGridCellWrapper';
import {
  GridApiPro,
  GridCallbackDetails,
  GridColDef,
  GridFilterModel,
  GridRenderHeaderFilterProps,
  GridRowParams,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import AurIconButton from '../../common/aur-button/AurIconButton';
import {ArrowDropDown, MoreVert} from '@mui/icons-material';
import {
  EvidenceStatuses,
  EvidenceSubStatus,
  INTAKE_STATUSES,
  TABLE_PAGE_SIZE_OPTION,
  TABLE_PER_PAGE_DEFAULT_NO_ROW,
} from '../../../constants/constants';
import AurPagination from '../../common/aur-pagination/AurPagination';
import {columnsConfiguration, mapEvidenceApiToGrid} from './constant';
import AurDataGridPro, {
  CustomGridColDef,
} from '../../common/aur-data-grid-pro/AurDataGridPro';
import FilterTextField from '../../common/aur-data-grid-pro/FilterTextField';
import FieldPopoverGridFilter, {
  FieldFilterGridData,
} from '../field-management/field-table/FieldPopoverGridFilter';
import {getUTCDateTimeForDisplay} from '../../../utils/utils';
import AurCustomSingleSelect from '../../common/aur-select-dropdown/AurCustomSingleSelect';
import AurAddCustodianModel from '../../common/aur-add-custodian-model/AurAddCustodianModel';
import EvidenceStatus from './EvidenceStatus';
import AurMenu from '../../common/aur-menu/AurMenu';
import InitiateProcessDrawer from './initiate-processing/InitiateProcessDrawer';
import {
  assignCustodian,
  getEvidenceSourceStaging,
} from '../../../api/EvidenceSourceUpload';
import {EvidenceParentRow} from '../../../types/evidence-source/evidenceSource';
import IntakeProgressModal from './intake-progress/IntakeProgressModal';
import {
  AssignCustodianPayload,
  CustodianPayload,
  ToastState,
} from '../../../types/upload-new-eviidence/evidenceSourceTypes';
import AurToast from '../../common/aur-toast/AurToast';
import {AurLightTooltip} from '../../common/aur-light-tooltip/aur-light-tooltip';
import {transformFilterModel, transformSortModel} from './EvidenceSourceUtils';
import {EvidenceStatusToLocaleKey} from './evidenceStatusHelper';
import {useEvidenceCustodian} from '../../../hooks/upload-evidence-source/useEvidenceCustodian';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import {showErrorToast} from '../../../api/utils';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {ReportScreenTabTypes} from '../reports/ProjectOverview';
import {useDispatch, useSelector} from 'react-redux';
import {setReportSrc} from '../../../redux/slices/analyticsSlice';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {useNavigate} from 'react-router-dom';
import {RootState} from '../../../redux/store';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';
import {
  clearWorkspaceDataSet,
  setMediaIDFromEvidenceGrid,
  setReportPageOpenFrom,
  setWorkspaceDataSetFromEvidenceGrid,
} from '../../../redux/slices/reportsSlice';
import {ManageEvidenceRefreshInterval} from '../../../config';
import {useAnalytics} from '../../../hooks/useAnalytics';

/**
 * Convert parent/child structure → flat tree rows
 */
/**
 * Convert parent/child → tree rows compatible with MUI treeData
 * - handles missing children
 * - ensures unique ids
 * - maps parent/child fields to grid columns
 */
const mergeSingleRow = (parent: any, media: any) => ({
  ...parent,
  clientRef: parent.clientRef || media.clientRef,
  status: parent.status || media.status,
  preProcessedSize: parent.preProcessedSize || media.preProcessedSize,
  mappedDataSize: parent.mappedDataSize || media.mappedDataSize,
  processingDatasetID: parent.processingDatasetID || media.processingDatasetID,
  deDupeMethod: parent.deDupeMethod || media.deDupeMethod,
  batchingGroup: media.batchingGroup,
});
export const buildTreeRows = (data: any[]) => {
  const rows: any[] = [];

  data.forEach((parent) => {
    const parentId = `p-${parent.id}`;

    // SINGLE => parent row
    if (parent.custodianType === 'Single') {
      const media = parent.children?.[0] || {};
      rows.push({
        ...mergeSingleRow(parent, media),
        id: parentId,
        hierarchy: [parentId],
        isParent: true,
        disableExpand: true,
      });
      return;
    }

    // MULTIPLE => parent row
    rows.push({
      ...parent,
      id: parentId,
      hierarchy: [parentId],
      isParent: true,
    });

    // Child header row → unique path
    rows.push({
      id: `ch-${parent.id}`,
      hierarchy: [parentId, 'header'], // unique
      isParent: false,
      isChildHeader: true,
    });

    // Children rows → unique paths
    (parent.children || []).forEach((child: any, index: number) => {
      const childId = `c-${parent.id}-${index}`;
      rows.push({
        ...child,
        id: childId,
        hierarchy: [parentId, childId], // unique
        isParent: false,
        evidMediaId: parent.evidMediaId, // for child rows without media-level data
        receiptMethod: parent.receiptMethod,
        safeID: parent.safeID,
        preProcessedSize: child.preProcessedSize ?? '',
        processingDatasetID:
          child.processingDatasetID || parent.processingDatasetID || '',
        clientRef: child.clientRef || parent.clientRef || '',
        status: child.status || parent.status || '',
        deDupeMethod: child.deDupeMethod || parent.deDupeMethod || '',
        custodianAssigned:
          child.custodianAssigned || parent.custodianAssigned || [],
        quadSRecord: parent.quadSRecord || '',
        batchingGroup: child.batchingGroup || null,
        evidenceSource: child.evidenceSource || parent.evidenceSource || '',
      });
    });
  });

  return rows;
};

const parseRowId = (rowId: string) => {
  if (rowId.startsWith('p-')) {
    return {
      type: 'parent' as const,
      parentId: Number(rowId.replace('p-', '')),
      childId: null,
    };
  }

  if (rowId.startsWith('c-')) {
    const [, parentId, childId] = rowId.split('-');
    return {
      type: 'child' as const,
      parentId: Number(parentId),
      childId: Number(childId),
    };
  }

  return null;
};

export interface FilterOptions {
  originatedUser?: string[];
  receipientMethod?: string[];
  custodianAssigned?: string[];
  status?: string[];
  dedupeMethod?: string[];
}
export interface AurMenuItem {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface SelectedProjectEvidenceType {
  evidMediaId: number;
  quadS: string;
  safeId: number;
  receiptMethod: string;
  evidenceSource: string;
  clientReferenceNumber: string;
  batchingGroup: number;
  dedupeMethod: string;
  custodianId: number;
}

const EvidenceSourceGrid = ({
  clientId,
  projectId,
  setListHasData,
  refreshTrigger,
  setIsRefreshing,
}: {
  clientId: string;
  projectId: string | undefined;
  setListHasData: (status: boolean) => void;
  refreshTrigger: number;
  setIsRefreshing: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const {trackGAEvent} = useAnalytics();

  const [showAddCustodian, setShowAddCustodian] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [gridData, setGridData] = useState<EvidenceParentRow[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'dateTime',
      sort: 'desc',
    },
  ]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(
    TABLE_PER_PAGE_DEFAULT_NO_ROW
  );
  const [totalRecords, setTotalRecords] = useState(0);
  const prevPayloadRef = useRef<any>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  // prevents filter crash without changing filter component
  const [gridReady, setGridReady] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(
    null
  );
  const activeRowRef = useRef<any | null>(null);
  const actionMenuSTRpath = 'evidenceSource.evidenceStatus.actionMenu';

  const [isInitiateProcessingDrawerOpen, setInitiateProcessingDrawerOpen] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [isAssignCustodianLoading, setIsAssignCustodianLoading] =
    useState(false);
  const [isCustodianAssigned, setIsCustodianAssigned] =
    useState<boolean>(false);

  const [selectedProjectEvidence, setSelectedProjectEvidence] =
    useState<SelectedProjectEvidenceType | null>(null);

  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [filterCount, setFilterCount] = useState<number>(0);
  const apiRef = useGridApiRef();
  const {showToast} = useToast();
  const EVIDENCE_STATUS_STRING_PATH = 'evidenceSource.evidenceStatus';

  const [showIntakeProgressModal, setShowIntakeProgressModal] =
    useState<boolean>(false);

  const [custodionList, setCustodionList] = useState<
    {id: string; label: string}[]
  >([]);
  const [selectedProgressRow, setSelectedProgressRow] =
    useState<EvidenceParentRow | null>(null);
  const previousFilterModelRef = useRef<GridFilterModel>({items: []});

  const renderEmptyHeaderFilter = (label: string) => (
    <span className="sr-only">{label}</span>
  );

  const {
    custodians,
    isLoading: isCustodianLoading,
    addNewCustodian,
  } = useEvidenceCustodian(clientId, projectId);

  const {
    canViewProgressReports,
    canViewProcessingSummaryReports,
    canViewCompletedMappedDataReports,
    canInitiateProcessing,
  } = usePermissionsByPage(PAGE_NAME.MANAGE_EVIDENCE_SOURCE, projectId);

  // paginate ONLY parents
  const paginatedParents = gridData;

  // build tree rows only from paginated parents
  const rows = useMemo(
    () => buildTreeRows(paginatedParents),
    [paginatedParents]
  );

  useEffect(() => {
    setCustodionList(
      custodians.map((item) => {
        return {
          id: item.id.toString(),
          label: item.displayName,
        };
      })
    );
  }, [custodians]);

  useEffect(() => {
    setGridReady(true);
  }, []);

  useEffect(() => {
    if (showAddCustodian || isInitiateProcessingDrawerOpen) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
        subPage: {
          subpage_name: showAddCustodian
            ? t('gaTracking.manageProjectEvidence.addCustodian')
            : t('gaTracking.manageProjectEvidence.initiateProcessing'),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
    }
  }, [showAddCustodian, isInitiateProcessingDrawerOpen]);

  const buildStagingPayload = () => ({
    sortOrder: transformSortModel(sortModel),
    filterSet: transformFilterModel(filterModel),
  });

  const changeFilterModel = (items: GridFilterModel) => {
    setFilterModel(items);
    setCurrentPage(1);
  };

  const assignCustodianRequest = async (payload: AssignCustodianPayload) => {
    try {
      setIsAssignCustodianLoading(true);
      const response = await assignCustodian(
        clientId,
        projectId ?? '',
        payload
      );
      if (_.get(response, 'data.meta.status', '') === 'Success') {
        showToast(_.get(response, 'data.meta.message', ''), 'success');
      }
      return Promise.resolve(true);
    } catch (error: unknown) {
      showErrorToast(showToast, error, 'right');
      return Promise.resolve(false);
    } finally {
      setIsAssignCustodianLoading(false);
    }
  };

  useEffect(() => {
    fetchEvidenceSourceData(false);
  }, [refreshTrigger]);

  const fetchEvidenceSourceData = async (
    checkDuplicate: boolean,
    refresh = false
  ) => {
    const payload = buildStagingPayload();

    const requestSignature = {
      payload,
      page: currentPage,
      pageSize: itemsPerPage,
    };

    const isSame =
      JSON.stringify(prevPayloadRef.current) ===
      JSON.stringify(requestSignature);

    if (isSame && checkDuplicate) {
      return; // prevents duplicate calls
    }

    prevPayloadRef.current = requestSignature;

    if (!refresh) {
      setIsLoading(true);
      setGridData([]);
      setIsRefreshing(true);
    }

    try {
      if (!refresh) setIsError(false);
      const paramString = `clientId=${clientId}&projectId=${projectId}&pageSize=${itemsPerPage || TABLE_PER_PAGE_DEFAULT_NO_ROW}&pageNumber=${currentPage}`;
      const res = await getEvidenceSourceStaging(paramString, payload);
      const optionsFromApi = res?.data?.data?.options ?? {};
      const mapped = mapEvidenceApiToGrid(
        res?.data?.data?.evidenceSourcesList || []
      );
      setGridData(mapped);
      setFilterOptions(optionsFromApi);
      setTotalRecords(res?.data?.data?.totalRecord || 0);
    } catch (err) {
      if (!refresh) setIsError(true);
    } finally {
      if (!refresh) {
        setIsLoading(false);
        setIsCustodianAssigned(false);
        setIsRefreshing(false);
      }
    }
  };

  useEffect(() => {
    if (!ManageEvidenceRefreshInterval) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      fetchEvidenceSourceData(false, true);
    }, ManageEvidenceRefreshInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [
    clientId,
    projectId,
    filterModel,
    sortModel,
    currentPage,
    itemsPerPage,
    refreshTrigger,
  ]);

  useEffect(() => {
    if (clientId && projectId) {
      fetchEvidenceSourceData(true);
      setFilterCount(filterModel?.items?.length || 0);
    }
  }, [clientId, projectId, filterModel, sortModel, currentPage, itemsPerPage]);

  useEffect(() => {
    // disable button if list has no data
    setListHasData(!isLoading && totalRecords > 0);
  }, [isLoading, totalRecords]);

  const getFilterOptions = (col: CustomGridColDef): FieldFilterGridData[] => {
    const fieldToOptionKeyMap: Record<string, keyof FilterOptions> = {
      originatingUser: 'originatedUser',
      receiptMethod: 'receipientMethod',
      custodianAssigned: 'custodianAssigned',
      status: 'status',
      deDupeMethod: 'dedupeMethod',
    };

    const mapToFilterData = (arr?: string[]) =>
      (arr || []).filter(Boolean).map((value) => ({
        id: value,
        value,
        label: value,
      }));

    const mapStatusToLocalized = (arr?: string[]) =>
      (arr || []).filter(Boolean).map((value) => {
        const enumStatus = value as EvidenceStatuses;
        const localeKey = EvidenceStatusToLocaleKey[enumStatus];
        return {
          id: value,
          value,
          label: localeKey
            ? t(`${EVIDENCE_STATUS_STRING_PATH}.statusFilter.${localeKey}`)
            : value,
        };
      });

    const optionKey = fieldToOptionKeyMap[col?.field];

    if (optionKey && filterOptions?.[optionKey]) {
      if (col?.field === 'status') {
        return mapStatusToLocalized(filterOptions[optionKey]);
      }

      return mapToFilterData(filterOptions[optionKey]);
    }

    return [];
  };

  const onClickTrackingEvent = (
    name: string,
    action: string,
    type: string,
    region: string,
    subPage?: {
      subpage_name: string;
      subpage_type: string;
    },
    eventName?: string
  ) => {
    const eventObject: any = {
      eventName: eventName ?? GA_EVENT_NAMES.CLICK,
      click: {
        name,
        type,
        region,
        action,
      },
    };

    if (subPage) {
      eventObject.subPage = subPage;
    }

    trackGAEvent(eventObject);
  };

  const getCustodianValue = (row: any) => {
    const custodianAssigned = _.get(row, 'custodianAssigned', []) as Array<{
      id: string;
      name: string;
    }>;
    // 1. When custodian type = Single and parent row as only single showing
    if (
      _.get(row, 'custodianType', '') === 'Single' &&
      _.get(row, 'isParent', false) &&
      custodianAssigned.length > 0
    ) {
      return {
        id: custodianAssigned[0].id,
        label: custodianAssigned[0].name,
      };
    }

    // 2. When custodian type = Multiple and child is expanded
    if (
      !_.get(row, 'isChildHeader', false) &&
      !_.get(row, 'isParent', false) &&
      custodianAssigned.length > 0
    ) {
      return {
        id: custodianAssigned[0].id,
        label: custodianAssigned[0].name,
      };
    }
    return null;
  };

  const NON_EDITABLE_STATUSES = new Set<EvidenceStatuses>([
    EvidenceStatuses.PROCESSED,
    EvidenceStatuses.IN_PROCESSING,
    EvidenceStatuses.PENDING_APPROVAL,
    EvidenceStatuses.IN_PROCESSING_MANUAL,
    ...INTAKE_STATUSES,
  ]);

  /**
   * Your existing columns
   */

  // first 5 column fields to blank for child + child header
  const CHILD_HIDE_FIELDS = columnsConfiguration
    .slice(0, 5)
    .map((c) => c.field);

  const columns: GridColDef[] = columnsConfiguration.map((col) => ({
    ...col,
    headerName: t(`evidenceSource.colHeaderNames.${col.headerName}`),
    showHeaderTooltip: true,

    //  mark first 5 blank columns
    headerClassName: CHILD_HIDE_FIELDS.includes(col.field)
      ? 'aur-blank-child-header'
      : '',

    cellClassName: (params) => {
      const classes: string[] = [];
      const isMultiRow = !params.row.disableExpand && params.row.isParent;

      if (
        col.field === 'custodianAssigned' &&
        !params.row.isChildHeader &&
        !isMultiRow
      ) {
        classes.push('custodian-cell');
      }

      if (CHILD_HIDE_FIELDS.includes(col.field)) {
        classes.push('aur-blank-child-cell');
      }

      return classes.join(' ');
    },

    renderCell: (params: any) => {
      const isChildRow = !params.row.isParent;
      const isChildHeader = params.row.isChildHeader;
      const isMultiRow = !params.row.disableExpand && params.row.isParent;
      if (
        (isChildRow || isChildHeader) &&
        CHILD_HIDE_FIELDS.includes(params.field)
      ) {
        return <span />; // keep spacing
      }

      const key = isChildHeader
        ? col?.childHeaderName || params.field
        : params.field;

      const label = t(`evidenceSource.colHeaderNames.${key}`);

      if (isChildHeader) {
        return (
          <AurLightTooltip title={label}>
            <span>{label}</span>
          </AurLightTooltip>
        );
      }

      if (col.field === 'status') {
        return (
          <EvidenceStatus
            status={params.value}
            subStatus={params.subtitle || 'INTAKE'}
          />
        );
      }

      if (col?.headerName === 'custodianAssigned') {
        if (isMultiRow) {
          return undefined; // for multi-row, show nothing in custodian cell and rely on child rows
        }
        return (
          <AurCustomSingleSelect
            value={getCustodianValue(params.row)}
            options={custodionList}
            addButtonLabel={t('evidenceSource.assignedCustodian.addCustodian')}
            placeholder={t('evidenceSource.assignedCustodian.pickCustodian')}
            ariaLabel={t('evidenceSource.assignedCustodian.addCustodian')}
            dataTestId={`custodian-cell-${params.row.id}`}
            hintText={t('evidenceSource.assignedCustodian.hintText')}
            onChange={(selected) => {
              onClickTrackingEvent(
                t('gaTracking.global.apply'),
                t('gaTracking.manageProjectEvidence.custodianChanged'),
                GA_CLICK_TYPES.BUTTON,
                GA_REGIONS.BODY
              );
              // update selected custodian to grid
              handleCustodianChange(
                params.row,
                selected as {id: string; label: string} | null
              );
            }}
            onOpen={() => {
              onClickTrackingEvent(
                t('gaTracking.manageProjectEvidence.custodian'),
                t('gaTracking.manageProjectEvidence.openedCustodianDropdown'),
                GA_CLICK_TYPES.DROPDOWN,
                GA_REGIONS.BODY
              );
            }}
            onAddCustodian={() => {
              onClickTrackingEvent(
                t('gaTracking.manageProjectEvidence.addCustodian'),
                t('gaTracking.manageProjectEvidence.addCustodianBegin'),
                GA_CLICK_TYPES.DROPDOWN,
                GA_REGIONS.BODY
              );
              setSelectedRowId(params.row.id);
              setShowAddCustodian(true);
            }}
            isReadOnly={NON_EDITABLE_STATUSES.has(params.row.status)}
            isDisabled={
              [
                ...INTAKE_STATUSES,
                EvidenceStatuses.IN_PROCESSING_MANUAL,
              ].includes(params.row.status) &&
              getCustodianValue(params.row) == null
            }
            isMultiRow={isMultiRow}
            handleMultiRow={() => {
              params.api?.setRowChildrenExpansion?.(params.row.id, true);
            }}
          />
        );
      }
      const displayValue =
        !params.row.isParent &&
        !params.row.isChildHeader &&
        col.field === 'custodianType'
          ? params.row.topLevelPath
          : params.value;
      return (
        <AurDataGridCellWrapper
          cellData={
            col.dataType === 'date'
              ? getUTCDateTimeForDisplay(displayValue)
              : displayValue
          }
          tooltipText={
            col.dataType === 'date' ? t('common.yearMonthDate') : displayValue
          }
        />
      );
    },
    renderHeaderFilter: col?.headerFilter
      ? (params: GridRenderHeaderFilterProps) =>
          gridReady && col.headerFilter
            ? (() => {
                switch (col.headerFilter) {
                  case 'search':
                    return (
                      <FilterTextField
                        gridRenderHeaderFilterProps={params}
                        placeholder={t('fieldManagement.fieldTable.search')}
                        parentClass="filter-input-field-container"
                        data-testid="term-input-filter"
                        applyOnEnterOnly
                      />
                    );
                  case 'date':
                    return (
                      <FieldPopoverGridFilter
                        apiRef={apiRef}
                        type="date"
                        columnField={col.field}
                        inputTabIndex={0}
                        dataTestId="date-filter"
                      />
                    );
                  case 'filter':
                    return (
                      <FieldPopoverGridFilter
                        key={col.field}
                        apiRef={apiRef}
                        columnField={col.field}
                        type="options"
                        dataArray={getFilterOptions(col)}
                        inputTabIndex={0}
                        dataTestId="term-input-filter"
                      />
                    );
                  default:
                    return undefined;
                }
              })()
            : undefined
      : () => renderEmptyHeaderFilter(t('common.filterNotAvailable')),
    filterable: Boolean(col?.headerFilter),
  }));

  const handleCustodianChange = (
    row: any,
    selected: {id: string; label: string} | null
  ) => {
    if (!selected) return;
    const {id: rowId, batchingGroup} = row;
    const parsed = parseRowId(rowId);
    if (!parsed) return;

    assignCustodianRequest({
      custodianId: parseInt(selected.id),
      batchingGroup: batchingGroup,
    }).then((result: boolean) => {
      if (!result) return;
      setGridData((prev) =>
        prev.map((parent) => {
          if (parent.id !== parsed.parentId) return parent;

          // 🔹 Parent Update
          if (parsed.type === 'parent') {
            return {
              ...parent,
              custodianAssigned: [
                {
                  id: selected.id,
                  name: selected.label,
                },
              ],
            };
          }

          // 🔹 Child Update
          if (parsed.type === 'child') {
            const updatedChildren = [...(parent.children ?? [])];
            if (_.get(updatedChildren, `[${parsed.childId}]`))
              updatedChildren[parsed.childId] = {
                ...updatedChildren[parsed.childId],
                custodianAssigned: [
                  {
                    id: selected.id,
                    name: selected.label,
                  },
                ],
              };
            return {
              ...parent,
              children: updatedChildren,
            };
          }
          return parent;
        })
      );
    });
  };

  const handleAddCustodian = async (value: CustodianPayload) => {
    if (!selectedRowId) return;

    const response = await addNewCustodian({
      firstName: value.firstName,
      lastName: value.lastName,
    });

    if (response.success) {
      showToast(response.message, 'success');
    } else {
      showToast(response.message, 'error');
    }

    setShowAddCustodian(false);
    setSelectedRowId(null);
  };

  /**
   * Action column
   */
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: any) => {
    event.stopPropagation();
    event.preventDefault();
    activeRowRef.current = row;
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setTimeout(() => {
      activeRowRef.current = null;
    }, 100);
  };

  const handleInitiateProcessing = (row: any) => {
    const custodianId = row.custodianAssigned[0]?.id;

    setInitiateProcessingDrawerOpen(true);
    setSelectedProjectEvidence({
      evidMediaId: row.evidMediaId,
      quadS: row.quadSRecord ?? '',
      safeId: row.safeID,
      receiptMethod: row.receiptMethod,
      evidenceSource: row.evidenceSource,
      clientReferenceNumber: row.clientRef,
      batchingGroup: row.batchingGroup,
      dedupeMethod: row.deDupeMethod,
      custodianId,
    });
  };
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const navigateToReport = (url: string, states?: any) => {
    dispatch(setReportPageOpenFrom('/manage-evidence'));
    dispatch(setReportSrc(GA_REGIONS?.BODY));
    if (states) {
      navigate(url, states);
    } else {
      navigate(url);
    }
  };
  const selectedProject = useSelector(
    (state: RootState) => state.uploadEvidenceSource?.selectedProject
  );
  const getMenuItems = (row: any): AurMenuItem[] => {
    const items: AurMenuItem[] = [];
    const statusVal = row?.status || '';

    const intakeStatuses = [
      EvidenceStatuses.INTAKE_DATA_COPYING,
      EvidenceStatuses.INTAKE_Extraction,
      EvidenceStatuses.INTAKE_VIRUS_SCANNING,
      EvidenceStatuses.INTAKE_COPY_TO_OUTPUT,
      EvidenceStatuses.INTAKE_BEING_PARSED,
      EvidenceStatuses.INTAKE_BATCHING,
    ];
    const isNonConsilioWorkflow =
      selectedProject?.isNonConsilioWorkflow ?? true;

    if (intakeStatuses.includes(statusVal)) {
      items.push({
        label: t(`${actionMenuSTRpath}.viewIntakeProgress`),
        onClick: () => {
          onClickTrackingEvent(
            t('gaTracking.manageProjectEvidence.viewIntakeProgress'),
            t(
              'gaTracking.manageProjectEvidence.actionMenuViewedProcessingSummary'
            ),
            GA_CLICK_TYPES.LINK,
            GA_REGIONS.BODY
          );
          setSelectedProgressRow(structuredClone(row));
          setShowIntakeProgressModal(true);
        },
      });
    }

    switch (statusVal) {
      case EvidenceStatuses.STAGED:
        if (canInitiateProcessing) {
          items.push({
            label: t(`${actionMenuSTRpath}.initiateProcessing`),
            onClick: () => {
              onClickTrackingEvent(
                t('gaTracking.manageProjectEvidence.initiateProcessing'),
                t('gaTracking.manageProjectEvidence.actionMenuProcessingBegin'),
                GA_CLICK_TYPES.LINK,
                GA_REGIONS.BODY
              );
              handleInitiateProcessing(row);
            },
          });
        }
        break;

      case EvidenceStatuses.IN_PROCESSING:
      case EvidenceStatuses.PENDING_APPROVAL:
      case EvidenceSubStatus.IN_PROCESSING_MANUAL:
        if (!isNonConsilioWorkflow && canViewProgressReports) {
          items.push({
            label: t(`${actionMenuSTRpath}.viewProcessingInProgress`),
            onClick: () => {
              onClickTrackingEvent(
                t('gaTracking.manageProjectEvidence.viewProcessingInProgress'),
                t(
                  'gaTracking.manageProjectEvidence.actionMenuViewedProcessingSummary'
                ),
                GA_CLICK_TYPES.LINK,
                GA_REGIONS.BODY
              );
              const {safeID} = row;
              if (safeID) {
                dispatch(setMediaIDFromEvidenceGrid({mediaId: safeID}));
              }
              navigateToReport(
                `/reports?tab=${ReportScreenTabTypes.DATA_SET_PROCESSING}`
              );
            },
          });
        }
        break;

      case EvidenceStatuses.PROCESSED:
        if (!isNonConsilioWorkflow && canViewProcessingSummaryReports) {
          items.push({
            label: t(`${actionMenuSTRpath}.viewProcessingSummaryReport`),
            onClick: () => {
              onClickTrackingEvent(
                t(
                  'gaTracking.manageProjectEvidence.viewProcessingSummaryReport'
                ),
                t(
                  'gaTracking.manageProjectEvidence.actionMenuViewedProcessingSummary'
                ),
                GA_CLICK_TYPES.LINK,
                GA_REGIONS.BODY
              );
              const {processingDatasetID, safeID} = row;
              const custodianId = _.get(row, 'custodianAssigned[0]', null);
              if (
                ((processingDatasetID as string) ?? '').split('-').length === 1
              ) {
                dispatch(
                  setWorkspaceDataSetFromEvidenceGrid({
                    workspaceDataSetName: processingDatasetID,
                    worksapceDataSetMediaId: safeID,
                    workspaceDatasetCustodianId: custodianId
                      ? parseInt(custodianId)
                      : null,
                  })
                );
              } else {
                dispatch(clearWorkspaceDataSet());
              }
              navigateToReport(
                `/reports?tab=${ReportScreenTabTypes.PROCESSED_DATASET}`
              );
            },
          });
        }
        break;

      case EvidenceStatuses.MAPPING_COMPLETE:
        if (canViewCompletedMappedDataReports) {
          items.push({
            label: t(`${actionMenuSTRpath}.viewCompleteMappedDataReport`),
            onClick: () => {
              onClickTrackingEvent(
                t(
                  'gaTracking.manageProjectEvidence.viewCompleteMappedDataReport'
                ),
                t(
                  'gaTracking.manageProjectEvidence.actionMenuViewedProcessingSummary'
                ),
                GA_CLICK_TYPES.LINK,
                GA_REGIONS.BODY
              );
              navigateToReport(
                `/reports?tab=${ReportScreenTabTypes.COMPLETED_MAPPED_DATA_REPORT}`
              );
            },
          });
        }
        break;
    }
    return items;
  };
  const actionColumn: GridColDef = {
    field: 'actions',
    headerName: '',
    width: 46,
    sortable: false,
    resizable: false,
    cellClassName: 'actions-rows-cell',
    renderCell: (params) => {
      if (params.row.isChildHeader) return null;
      const menuItems = getMenuItems(params.row);
      if (!menuItems || menuItems.length === 0) return null;

      return (
        <AurIconButton
          className="grid-action-button"
          icon={<MoreVert aria-hidden="true" />}
          onClick={(event) => {
            event.stopPropagation();
            onClickTrackingEvent(
              t('gaTracking.global.actionIcon'),
              t('gaTracking.global.actionMenuOpened'),
              GA_CLICK_TYPES.ICON,
              GA_REGIONS.BODY
            );
            handleMenuOpen(event, params.row);
          }}
          ariaLabel={t(
            'evidenceSource.evidenceStatus.actionMenu.ariaLabelViewDetail'
          )}
        />
      );
    },
    renderHeader: () => <span className="sr-only">{t('common.actions')}</span>,
  };

  const renderPaginationBlock = () => (
    <Box className="table-pagination-container">
      <Box className="table-pagination-control">
        <AurPagination
          totalItems={totalRecords}
          itemType={t('evidenceSource.evidenceSources')}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          perPageOptions={TABLE_PAGE_SIZE_OPTION}
          onPageChange={setCurrentPage}
          setItemsPerPage={(number) => {
            setItemsPerPage(number);
            setCurrentPage(1);
          }}
          ariaLabelNextPage={t('common.pagination.next')}
          ariaLabelPreviousPage={t('common.pagination.previous')}
          ariaDescribedByPreviousPage={t('common.pagination.previousHint')}
          ariaDescribedByNextPage={t('common.pagination.nextHint')}
          appliedFilterCount={filterCount}
          onClearFilterClick={() => {
            changeFilterModel({items: []});
          }}
        />
      </Box>
    </Box>
  );

  const callFilterGATrackingEvent = (items: GridFilterModel) => {
    const previousFilters = previousFilterModelRef.current.items.filter(
      (item) =>
        item.value !== undefined && item.value !== null && item.value !== ''
    );

    const currentFilters = items.items.filter(
      (item) =>
        item.value !== undefined && item.value !== null && item.value !== ''
    );

    const isFilterRemoved = currentFilters.length < previousFilters.length;

    if (!isFilterRemoved) {
      const changedFilter = currentFilters.find((current) => {
        const previous = previousFilters.find((p) => p.field === current.field);

        return !previous || previous.value !== current.value;
      });

      if (changedFilter) {
        const column = columnsConfiguration.find(
          (col) => col.field === changedFilter.field
        );

        onClickTrackingEvent(
          t(`evidenceSource.colHeaderNames.${changedFilter.field}`),
          t('gaTracking.manageProjectEvidence.filteredEvidenceSourcesList'),
          column?.headerFilter === 'search'
            ? GA_CLICK_TYPES.INPUT
            : GA_CLICK_TYPES.DROPDOWN,
          GA_REGIONS.BODY,
          undefined,
          GA_EVENT_NAMES.FILTER
        );
      }
    }

    previousFilterModelRef.current = items;
  };

  /**
   * Tree grouping column customization
   */
  const groupingColDef = {
    headerName: '',
    width: 48,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    headerClassName: 'aur-parent-header',
    className: 'aur-data-grid-pro-filter',
    cellClassName: (params: any) =>
      !params.row.isParent
        ? 'aur-blank-child-cell grp-col-icon'
        : 'grp-col-icon',
    renderHeader: () => (
      <span className="sr-only">
        {t('evidenceSource.evidenceStatus.actionMenu.ariaLabelAccrodian')}
      </span>
    ),
    renderHeaderFilter: () => (
      <span className="sr-only">{t('common.filterNotAvailable')}</span>
    ),
    renderCell: (params: any) => {
      const hasChildren =
        params.rowNode?.type === 'group' && !params.row.disableExpand;
      const expanded = params.rowNode?.childrenExpanded;

      if (!hasChildren || !params.row.isParent || params.row.isChildHeader) {
        return null;
      }

      return (
        <AurIconButton
          className="tree-toggle-btn"
          icon={
            <ArrowDropDown
              className={`tree-toggle-icon ${expanded ? 'expanded' : ''}`} aria-hidden="true"
            />
          }
          onClick={(e) => {
            e.stopPropagation();
            params.api.setRowChildrenExpansion(params.id, !expanded);
          }}
          ariaLabel={t(
            'evidenceSource.evidenceStatus.actionMenu.ariaLabelAccrodian'
          )}
          ariaDescribedBy={t(
            'evidenceSource.evidenceStatus.actionMenu.ariaDesribedByAccordian'
          )}
        />
      );
    },
  };

  const handleOnProcessingInitiated = () => {
    setGridData([]);
    setIsLoading(true);
    setInitiateProcessingDrawerOpen(false);

    setToast({
      open: true,
      severity: 'success',
      message: t('evidenceSource.initiateProcess.successMessage', {
        dataset_name: selectedProjectEvidence?.evidenceSource ?? '',
      }),
    });

    setTimeout(() => {
      fetchEvidenceSourceData(false);
    }, 3000);

    setSelectedProjectEvidence(null);
  };

  const getTreeDataPath = useCallback((row: any) => row.hierarchy, []);
  return (
    <Box className="evidence-source-grid-container aur-tree-data-grid-pro">
      {renderPaginationBlock()}

      <AurDataGridPro
        disableVirtualization
        rows={rows}
        columns={[...columns, actionColumn]}
        className="aur-data-grid-pro-filter"
        apiRef={apiRef}
        treeData
        getTreeDataPath={getTreeDataPath}
        defaultGroupingExpansionDepth={0} // collapsed initially
        disableRowSelectionOnClick
        headerFilters={gridReady}
        hideFooter
        columnHeaderHeight={64}
        headerFilterHeight={48}
        rowHeight={64}
        filterDebounceMs={500}
        pageSizeOptions={TABLE_PAGE_SIZE_OPTION}
        initialState={{
          pinnedColumns: {right: ['actions']},
        }}
        scrollbarSize={17}
        groupingColDef={groupingColDef}
        getRowClassName={(params) => {
          const classes = [];

          if (params.row.isChildHeader) {
            classes.push('aur-child-header-row');
          }

          if (!params.row.isParent && !params.row.isChildHeader) {
            classes.push('aur-child-row');
          }

          if (params.row.isParent) {
            classes.push('aur-parent-row');
            if (params.row.disableExpand) {
              classes.push('aur-single-row');
            }
          }

          return classes.join(' ');
        }}
        onRowClick={(
          params: GridRowParams,
          event,
          details: GridCallbackDetails
        ) => {
          if (params.row.isChildHeader) {
            event.stopPropagation();
            return;
          }

          const isMultiRow = !params.row.disableExpand && params.row.isParent;
          if (!isMultiRow) return;

          // Don't expand when clicking Actions column
          const cell = (event.target as HTMLElement).closest(
            '[data-field="actions"]'
          );

          if (cell) {
            return;
          }

          const api = details.api as GridApiPro;

          const node = api.getRowNode(params.id);

          const expanded =
            node && 'childrenExpanded' in node ? node.childrenExpanded : false;

          api.setRowChildrenExpansion(params.id, !expanded);
        }}
        filterModel={filterModel}
        onFilterModelChange={(items) => {
          callFilterGATrackingEvent(items);
          changeFilterModel(items);
        }}
        filterMode="server"
        checkBoxVisible={false}
        // sorting
        sortModel={sortModel}
        setSortModel={(model) => {
          setSortModel(model);
          setCurrentPage(1);
        }}
        sortingMode="server"
        disableColumnSorting={false} // keep menu
        isReqLoading={isLoading}
        haveReqError={isError}
        onErrorRefresh={() => fetchEvidenceSourceData(false)}
        showSortIconOnHover
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
      />
      {menuAnchor && activeRowRef.current && (
        <AurMenu
          shouldShow={Boolean(menuAnchor)}
          anchorElement={menuAnchor}
          direction="right"
          onClose={handleMenuClose}
          menuItems={getMenuItems(activeRowRef.current).map((item) => ({
            ...item,
            onClick: () => {
              item.onClick();
              handleMenuClose();
            },
          }))}
        />
      )}
      {renderPaginationBlock()}

      {isInitiateProcessingDrawerOpen && (
        <InitiateProcessDrawer
          open={isInitiateProcessingDrawerOpen}
          onClose={() => {
            setInitiateProcessingDrawerOpen(false);
            isCustodianAssigned && fetchEvidenceSourceData(false);
          }}
          projectEvidenceDetails={selectedProjectEvidence}
          onProcessingInitiated={handleOnProcessingInitiated}
          onCustodianAssigned={() => setIsCustodianAssigned(true)}
        />
      )}
      {showAddCustodian && (
        <AurAddCustodianModel
          isOpen={showAddCustodian}
          onClose={() => {
            onClickTrackingEvent(
              t('gaTracking.global.close'),
              t('gaTracking.manageProjectEvidence.addCustodianClosedModal'),
              GA_CLICK_TYPES.BUTTON,
              GA_REGIONS.MODAL,
              {
                subpage_name: t(
                  'gaTracking.manageProjectEvidence.addCustodian'
                ),
                subpage_type: GA_SUBPAGE_TYPE.MODAL,
              }
            );
            setShowAddCustodian(false);
          }}
          keepMounted
          onAddClick={(value: any) => {
            onClickTrackingEvent(
              t('gaTracking.global.add'),
              t('gaTracking.manageProjectEvidence.addCustodianComplete'),
              GA_CLICK_TYPES.BUTTON,
              GA_REGIONS.MODAL,
              {
                subpage_name: t(
                  'gaTracking.manageProjectEvidence.addCustodian'
                ),
                subpage_type: GA_SUBPAGE_TYPE.MODAL,
              }
            );
            // call api for selectedRowId with value
            handleAddCustodian(value);
          }}
        />
      )}

      {showIntakeProgressModal && selectedProgressRow && (
        <IntakeProgressModal
          open={showIntakeProgressModal}
          onClose={() => {
            setShowIntakeProgressModal(false);
            setSelectedProgressRow(null);
          }}
          clientId={clientId}
          projectId={projectId}
          selectedRow={selectedProgressRow}
        />
      )}

      <AurToast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        hideDuration={5000}
        horizontalPosition="center"
        verticalPosition="bottom"
        onClose={() => setToast((prev) => ({...prev, open: false}))}
      />
      <AurLoadingIndicator
        isOpen={isAssignCustodianLoading || isCustodianLoading}
        overlay
      />
    </Box>
  );
};

export default EvidenceSourceGrid;
